import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Upload, Edit, Trash2, Search, LogOut, Download, Image as ImageIcon, Info, Mail, Play, Lock, ShoppingBag } from 'lucide-react';
import { Product, ColorVariant } from '../data/products';
import { listProducts } from '../api/products';
import { buildApiUrl } from '../config';

const parseHighlightsInput = (value: string): string[] => {
  return value
    .split(/\r?\n|\|/)
    .map((s) => s.trim())
    .filter(Boolean);
};

const parseSpecificationsInput = (value: string): Record<string, string> => {
  const trimmed = value.trim();
  if (!trimmed) {
    return {};
  }
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return Object.keys(parsed).reduce((acc, key) => {
        const rawValue = parsed[key];
        if (rawValue !== undefined && rawValue !== null && rawValue !== '') {
          acc[String(key).trim()] =
            typeof rawValue === 'string' ? rawValue : String(rawValue);
        }
        return acc;
      }, {} as Record<string, string>);
    }
  } catch {
    // fall back to parsing as key:value pairs
  }

  const specObj: Record<string, string> = {};
  trimmed.split(/\r?\n|\|/).forEach((line) => {
    const str = line.trim();
    if (!str) return;
    const idx = str.indexOf(':');
    if (idx > -1) {
      const key = str.slice(0, idx).trim();
      const val = str.slice(idx + 1).trim();
      if (key && val) {
        specObj[key] = val;
      }
    }
  });
  return specObj;
};

interface ColorVariantsSectionProps {
  variants: ColorVariant[];
  onChange: (variants: ColorVariant[]) => void;
  authenticatedFetch: (url: string, options?: RequestInit) => Promise<Response>;
}

const ColorVariantsSection: React.FC<ColorVariantsSectionProps> = ({ variants, onChange, authenticatedFetch }) => {
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);

  const handleAddVariant = () => {
    const newVariant: ColorVariant = {
      name: '',
      colorCode: '#4554a4',
      image: '',
      images: [],
      isDefault: variants.length === 0,
    };
    onChange([...variants, newVariant]);
  };

  const handleUpdateVariant = (index: number, updatedFields: Partial<ColorVariant>) => {
    const updated = variants.map((v, i) => {
      if (i === index) {
        return { ...v, ...updatedFields };
      }
      if (updatedFields.isDefault && i !== index) {
        return { ...v, isDefault: false };
      }
      return v;
    });
    onChange(updated);
  };

  const handleRemoveVariant = (index: number) => {
    const updated = variants.filter((_, i) => i !== index);
    if (variants[index]?.isDefault && updated.length > 0) {
      updated[0].isDefault = true;
    }
    onChange(updated);
  };

  const handleUploadMainImage = async (index: number, file: File) => {
    try {
      setUploadingIndex(index);
      const formData = new FormData();
      formData.append('image', file);
      const res = await authenticatedFetch(buildApiUrl('/api/images/upload'), {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (data.url) {
        handleUpdateVariant(index, { image: data.url });
      } else {
        alert(data.message || 'Failed to upload image');
      }
    } catch (err) {
      console.error('Variant main image upload error:', err);
      alert('Error uploading variant image');
    } finally {
      setUploadingIndex(null);
    }
  };

  const handleUploadGalleryImages = async (index: number, files: FileList) => {
    try {
      setUploadingIndex(index);
      const currentImages = [...(variants[index].images || [])];
      for (let i = 0; i < files.length; i++) {
        const formData = new FormData();
        formData.append('image', files[i]);
        const res = await authenticatedFetch(buildApiUrl('/api/images/upload'), {
          method: 'POST',
          body: formData,
        });
        const data = await res.json();
        if (data.url) {
          currentImages.push(data.url);
        }
      }
      handleUpdateVariant(index, { images: currentImages });
    } catch (err) {
      console.error('Variant gallery image upload error:', err);
      alert('Error uploading variant gallery image');
    } finally {
      setUploadingIndex(null);
    }
  };

  const handleAddGalleryUrl = (index: number, url: string) => {
    if (!url.trim()) return;
    const currentImages = [...(variants[index].images || []), url.trim()];
    handleUpdateVariant(index, { images: currentImages });
  };

  const handleRemoveGalleryImage = (variantIndex: number, imgIndex: number) => {
    const currentImages = (variants[variantIndex].images || []).filter((_, i) => i !== imgIndex);
    handleUpdateVariant(variantIndex, { images: currentImages });
  };

  return (
    <div className="border-b pb-6 mb-6">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Product Color Variants</h3>
          <p className="text-xs text-gray-500">Add color options (e.g. Carbon Black, Teal Blue) with thumbnail images & color-specific galleries.</p>
        </div>
        <button
          type="button"
          onClick={handleAddVariant}
          className="inline-flex items-center px-3 py-1.5 text-xs font-semibold text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors shadow-sm"
        >
          + Add Color Variant
        </button>
      </div>

      {variants.length === 0 ? (
        <div className="bg-gray-50 border border-dashed border-gray-300 rounded-lg p-6 text-center">
          <p className="text-sm text-gray-500 font-medium">No color variants added for this product yet.</p>
          <p className="text-xs text-gray-400 mt-1">Click "+ Add Color Variant" above to add color choices (e.g. Black, Blue, Red) with specific images.</p>
        </div>
      ) : (
        <div className="space-y-4 mt-3">
          {variants.map((variant, vIdx) => (
            <div key={vIdx} className="bg-slate-50 border border-slate-200 rounded-lg p-4 relative shadow-sm">
              <div className="flex items-center justify-between mb-3 border-b pb-2">
                <div className="flex items-center space-x-2">
                  <div
                    className="w-5 h-5 rounded-full border border-gray-300 shadow-inner"
                    style={{ backgroundColor: variant.colorCode || '#4554a4' }}
                  />
                  <span className="font-semibold text-sm text-gray-800">
                    Variant #{vIdx + 1}: {variant.name || 'Unnamed Color'}
                  </span>
                  {variant.isDefault && (
                    <span className="bg-blue-100 text-blue-800 text-[10px] font-semibold px-2 py-0.5 rounded">
                      Default Selected
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveVariant(vIdx)}
                  className="text-red-600 hover:text-red-800 text-xs font-medium"
                >
                  Remove Variant
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                {/* Color Name */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Color Name *</label>
                  <input
                    type="text"
                    value={variant.name}
                    onChange={(e) => handleUpdateVariant(vIdx, { name: e.target.value })}
                    placeholder="e.g. Carbon Black"
                    className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-indigo-500"
                    required
                  />
                </div>

                {/* Color Code / Hex */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Color Swatch (Hex Code / Picker)</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={variant.colorCode || '#4554a4'}
                      onChange={(e) => handleUpdateVariant(vIdx, { colorCode: e.target.value })}
                      className="w-8 h-8 rounded border border-gray-300 cursor-pointer p-0"
                    />
                    <input
                      type="text"
                      value={variant.colorCode || ''}
                      onChange={(e) => handleUpdateVariant(vIdx, { colorCode: e.target.value })}
                      placeholder="#1A1A1A"
                      className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-md font-mono"
                    />
                  </div>
                </div>

                {/* Default Variant Checkbox */}
                <div className="flex items-center pt-5">
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!variant.isDefault}
                      onChange={(e) => handleUpdateVariant(vIdx, { isDefault: e.target.checked })}
                      className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-xs font-medium text-gray-700">Default Selected Variant</span>
                  </label>
                </div>
              </div>

              {/* Main Variant Thumbnail Image */}
              <div className="border border-gray-200 rounded-md bg-white p-3 mb-3">
                <label className="block text-xs font-semibold text-gray-800 mb-1">
                  Variant Thumbnail Image (Displayed in Color Selector Button)
                </label>
                <div className="flex items-center space-x-3">
                  <div className="w-14 h-14 border rounded-md bg-gray-100 overflow-hidden flex items-center justify-center flex-shrink-0">
                    {variant.image ? (
                      <img src={variant.image} alt={variant.name} className="w-full h-full object-contain" />
                    ) : (
                      <span className="text-[10px] text-gray-400 text-center">No image</span>
                    )}
                  </div>
                  <div className="flex-1 space-y-1.5">
                    <div className="flex items-center space-x-2">
                      <input
                        type="file"
                        accept="image/*"
                        id={`variant-main-file-${vIdx}`}
                        onChange={(e) => {
                          if (e.target.files?.[0]) {
                            handleUploadMainImage(vIdx, e.target.files[0]);
                          }
                        }}
                        className="hidden"
                      />
                      <label
                        htmlFor={`variant-main-file-${vIdx}`}
                        className="px-2.5 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 rounded cursor-pointer transition-colors"
                      >
                        {uploadingIndex === vIdx ? 'Uploading...' : '📁 Upload Thumbnail File'}
                      </label>
                      <span className="text-xs text-gray-400">or paste image URL:</span>
                    </div>
                    <input
                      type="text"
                      value={variant.image || ''}
                      onChange={(e) => handleUpdateVariant(vIdx, { image: e.target.value })}
                      placeholder="https://... or /uploads/images/..."
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                    />
                  </div>
                </div>
              </div>

              {/* Color-Specific Gallery Images */}
              <div className="border border-gray-200 rounded-md bg-white p-3 mb-3">
                <label className="block text-xs font-semibold text-gray-800 mb-1">
                  Color-Specific Detail Images (Swaps main gallery when this color is active)
                </label>
                
                {/* Existing Gallery Images */}
                {variant.images && variant.images.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2 p-2 bg-gray-50 rounded border">
                    {variant.images.map((imgUrl, imgIdx) => (
                      <div key={imgIdx} className="relative w-12 h-12 border rounded bg-white overflow-hidden group">
                        <img src={imgUrl} alt={`Gallery ${imgIdx}`} className="w-full h-full object-contain" />
                        <button
                          type="button"
                          onClick={() => handleRemoveGalleryImage(vIdx, imgIdx)}
                          className="absolute top-0 right-0 bg-red-600 text-white rounded-bl w-4 h-4 text-[10px] flex items-center justify-center opacity-80 group-hover:opacity-100"
                          title="Remove image"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex items-center space-x-2">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    id={`variant-gallery-files-${vIdx}`}
                    onChange={(e) => {
                      if (e.target.files && e.target.files.length > 0) {
                        handleUploadGalleryImages(vIdx, e.target.files);
                      }
                    }}
                    className="hidden"
                  />
                  <label
                    htmlFor={`variant-gallery-files-${vIdx}`}
                    className="px-2.5 py-1 text-xs bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 rounded cursor-pointer transition-colors"
                  >
                    + Upload Gallery Files
                  </label>
                  <input
                    type="text"
                    placeholder="Paste image URL and press Enter..."
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const input = e.currentTarget;
                        handleAddGalleryUrl(vIdx, input.value);
                        input.value = '';
                      }
                    }}
                    className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
                  />
                </div>
              </div>

              {/* Optional Overrides (Price & Links) */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-1 border-t border-gray-200">
                <div>
                  <label className="block text-[11px] text-gray-500 mb-0.5">Price (Optional override)</label>
                  <input
                    type="number"
                    value={variant.price !== undefined ? variant.price : ''}
                    onChange={(e) => handleUpdateVariant(vIdx, { price: e.target.value !== '' ? Number(e.target.value) : undefined })}
                    placeholder="Same as base"
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-500 mb-0.5">Original Price (Optional)</label>
                  <input
                    type="number"
                    value={variant.originalPrice !== undefined ? variant.originalPrice : ''}
                    onChange={(e) => handleUpdateVariant(vIdx, { originalPrice: e.target.value !== '' ? Number(e.target.value) : undefined })}
                    placeholder="Same as base"
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-500 mb-0.5">Amazon Link (Optional)</label>
                  <input
                    type="text"
                    value={variant.amazonLink || ''}
                    onChange={(e) => handleUpdateVariant(vIdx, { amazonLink: e.target.value })}
                    placeholder="Same as base"
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-500 mb-0.5">Flipkart Link (Optional)</label>
                  <input
                    type="text"
                    value={variant.flipkartLink || ''}
                    onChange={(e) => handleUpdateVariant(vIdx, { flipkartLink: e.target.value })}
                    placeholder="Same as base"
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AdminPanel: React.FC = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showImageUploadModal, setShowImageUploadModal] = useState(false);
  const [showImageSpecs, setShowImageSpecs] = useState(false);
  const [imageSpecs, setImageSpecs] = useState<any>(null);
  const [newProductImage, setNewProductImage] = useState<File | null>(null);
  const [newProductImagePreview, setNewProductImagePreview] = useState<string>('');
  const [newProductThumbnails, setNewProductThumbnails] = useState<File[]>([]);
  const [newProductBanners, setNewProductBanners] = useState<File[]>([]);
  const [newProductVideos, setNewProductVideos] = useState<File[]>([]);
  const [editingProductImage, setEditingProductImage] = useState<File | null>(null);
  const [editingProductImagePreview, setEditingProductImagePreview] = useState<string>('');
  const [editingProductThumbnails, setEditingProductThumbnails] = useState<File[]>([]);
  const [editingProductBanners, setEditingProductBanners] = useState<File[]>([]);
  const [editingProductVideos, setEditingProductVideos] = useState<File[]>([]);
  const [editingProductMedia, setEditingProductMedia] = useState<{ images: string[]; banners: string[]; videos: string[] }>({ images: [], banners: [], videos: [] });
  const [deletingMedia, setDeletingMedia] = useState<string | null>(null);
  const [playingVideo, setPlayingVideo] = useState<string | null>(null);
  const [selectedZipFile, setSelectedZipFile] = useState<File | null>(null);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [showDeleteProductsConfirm, setShowDeleteProductsConfirm] = useState(false);
  const [isDeletingProducts, setIsDeletingProducts] = useState(false);
  const [showDeleteImagesConfirm, setShowDeleteImagesConfirm] = useState(false);
  const [isDeletingImages, setIsDeletingImages] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [passwordResetData, setPasswordResetData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordResetError, setPasswordResetError] = useState('');
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [totalProducts, setTotalProducts] = useState(0);
  const [newHighlightsInput, setNewHighlightsInput] = useState('');
  const [newSpecificationsInput, setNewSpecificationsInput] = useState('');
  const [editingHighlightsInput, setEditingHighlightsInput] = useState('');
  const [editingSpecificationsInput, setEditingSpecificationsInput] = useState('');

  const bannerCacheBust = useMemo(() => {
    if (editingProduct?.updatedAt) {
      return new Date(editingProduct.updatedAt).getTime();
    }
    return Date.now();
  }, [editingProduct?.updatedAt, showEditModal]);
  const productsPerPage = 20;
  const [newProduct, setNewProduct] = useState<Partial<Product>>({
    title: '',
    description: '',
    shortDescription: '',
    image: '',
    price: 0,
    originalPrice: 0,
    category: '',
    amazonLink: '',
    flipkartLink: '',
    videoUrl: '',
    highlights: [],
    specifications: {},
    rating: 0,
    reviews: 0,
    slug: '',
    featured: false,
  });

  useEffect(() => {
    if (showAddModal) {
      const highlightStr = Array.isArray(newProduct.highlights)
        ? newProduct.highlights.join('\n')
        : '';
      let specsStr = '';
      if (
        newProduct.specifications &&
        typeof newProduct.specifications === 'object' &&
        Object.keys(newProduct.specifications as Record<string, unknown>).length > 0
      ) {
        specsStr = JSON.stringify(newProduct.specifications, null, 2);
      }
      setNewHighlightsInput(highlightStr);
      setNewSpecificationsInput(specsStr);
    } else {
      setNewHighlightsInput('');
      setNewSpecificationsInput('');
    }
  }, [showAddModal, newProduct.highlights, newProduct.specifications]);

  useEffect(() => {
    if (showEditModal && editingProduct) {
      const highlightStr = Array.isArray(editingProduct.highlights)
        ? editingProduct.highlights.join('\n')
        : typeof editingProduct.highlights === 'string'
          ? editingProduct.highlights
          : '';
      let specsStr = '';
      if (
        editingProduct.specifications &&
        typeof editingProduct.specifications === 'object' &&
        Object.keys(editingProduct.specifications as Record<string, unknown>).length > 0
      ) {
        specsStr = JSON.stringify(editingProduct.specifications, null, 2);
      } else if (typeof editingProduct.specifications === 'string') {
        specsStr = editingProduct.specifications;
      }
      setEditingHighlightsInput(highlightStr);
      setEditingSpecificationsInput(specsStr);
    } else if (!showEditModal) {
      setEditingHighlightsInput('');
      setEditingSpecificationsInput('');
    }
  }, [showEditModal, editingProduct]);

  useEffect(() => {
    if (!localStorage.getItem('admin_token')) {
      navigate('/admin/login');
      return;
    }
    loadProducts(1);
    loadImageSpecs();
  }, [navigate]);

  // Reload when filters change
  useEffect(() => {
    if (localStorage.getItem('admin_token')) {
      loadProducts(1);
    }
  }, [selectedCategory, searchTerm]);

  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      throw new Error('No token found');
    }

    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`
    } as HeadersInit;

    const response = await fetch(url, { ...options, headers });

    if (response.status === 401) {
      localStorage.removeItem('admin_token');
      navigate('/admin/login');
      throw new Error('Session expired');
    }

    return response;
  };

  const loadImageSpecs = async () => {
    try {
      // Public endpoint, no auth needed
      const response = await fetch(buildApiUrl('/api/images/specs'));
      if (response.ok) {
        const specs = await response.json();
        setImageSpecs(specs);
      }
    } catch (error) {
      console.error('Failed to load image specs:', error);
    }
  };

  const handleImageZipUpload = async (file: File) => {
    setIsUploadingImages(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await authenticatedFetch(buildApiUrl('/api/images/upload-zip'), {
        method: 'POST',
        body: formData,
      });
      if (response.ok) {
        const data = await response.json();
        setShowImageUploadModal(false);
        setSelectedZipFile(null);
        setIsUploadingImages(false);
        if (data.updated) {
          alert(`✅ Success!\n\nProcessed: ${data.processed} images\nMatched & Updated: ${data.updated} products\n\nImages have been automatically linked to matching products!`);
        } else {
          alert(`✅ Successfully processed ${data.processed} images!\n\nNote: ${data.matched || 0} products matched but may need manual linking.`);
        }
        loadProducts();
      } else {
        const error = await response.json();
        setIsUploadingImages(false);
        alert(error.message || 'Image upload failed');
      }
    } catch (error) {
      console.error('Failed to upload images:', error);
      setIsUploadingImages(false);
      alert('Upload failed');
    }
  };

  const downloadSampleCSV = () => {
    window.open(buildApiUrl('/api/products/sample-csv'), '_blank');
  };

  const downloadTestCSV = () => {
    window.open(buildApiUrl('/api/products/test-csv'), '_blank');
  };

  const handleDeleteAllProducts = async () => {
    setIsDeletingProducts(true);
    try {
      const response = await authenticatedFetch(buildApiUrl('/api/products/all'), {
        method: 'DELETE',
      });

      if (response.ok) {
        const data = await response.json();
        setShowDeleteProductsConfirm(false);
        alert(`✅ Successfully deleted!\n\nProducts: ${data.productsDeleted}\nLocal Images: ${data.localImagesDeleted}\nAzure Images: ${data.azureImagesDeleted}\n\nTotal Images: ${data.totalImagesDeleted}${data.errors ? '\n\nSome errors occurred, check console.' : ''}`);
        loadProducts(1); // Reload product list
      } else {
        const error = await response.json();
        alert(error.message || 'Failed to delete products');
      }
    } catch (error) {
      console.error('Failed to delete products:', error);
      alert('Failed to delete products');
    } finally {
      setIsDeletingProducts(false);
    }
  };

  const handleDeleteAllImages = async () => {
    setIsDeletingImages(true);
    try {
      const response = await authenticatedFetch(buildApiUrl('/api/images/all'), {
        method: 'DELETE',
      });

      if (response.ok) {
        const data = await response.json();
        setShowDeleteImagesConfirm(false);
        alert(`✅ Successfully deleted all images!\n\nLocal Images: ${data.localDeleted}\nAzure Images: ${data.azureDeleted}\n\nTotal: ${data.totalDeleted}${data.errors ? '\n\nSome errors occurred, check console.' : ''}`);
      } else {
        const error = await response.json();
        alert(error.message || 'Failed to delete images');
      }
    } catch (error) {
      console.error('Failed to delete images:', error);
      alert('Failed to delete images');
    } finally {
      setIsDeletingImages(false);
    }
  };

  const loadProducts = async (page: number = currentPage) => {
    setLoading(true);
    try {
      const data = await listProducts({
        page,
        limit: productsPerPage,
        ...(selectedCategory ? { category: selectedCategory } : {}),
        ...(searchTerm ? { search: searchTerm } : {})
      });
      setProducts(data.items);
      setTotalPages(data.pages);
      setTotalProducts(data.total);
      setCurrentPage(data.page);
    } catch (error) {
      console.error('Failed to load products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const handlePasswordReset = async () => {
    setPasswordResetError('');

    // Validation
    if (!passwordResetData.currentPassword || !passwordResetData.newPassword || !passwordResetData.confirmPassword) {
      setPasswordResetError('All fields are required');
      return;
    }

    if (passwordResetData.newPassword.length < 6) {
      setPasswordResetError('New password must be at least 6 characters');
      return;
    }

    if (passwordResetData.newPassword !== passwordResetData.confirmPassword) {
      setPasswordResetError('New passwords do not match');
      return;
    }

    setIsResettingPassword(true);
    try {
      const response = await authenticatedFetch(buildApiUrl('/api/auth/reset-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          currentPassword: passwordResetData.currentPassword,
          newPassword: passwordResetData.newPassword,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        alert('Password reset successfully! Please login again with your new password.');
        setShowPasswordResetModal(false);
        setPasswordResetData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        handleLogout();
      } else {
        setPasswordResetError(data.message || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Failed to reset password:', error);
      setPasswordResetError('Failed to reset password. Please try again.');
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleAddProduct = async () => {
    try {
      const formData = new FormData();

      // Append all product fields
      Object.keys(newProduct).forEach(key => {
        if (key === 'highlights' || key === 'specifications') {
          return;
        }
        const value = newProduct[key as keyof typeof newProduct];
        if (value !== undefined && value !== null && value !== '') {
          if (key === 'category') {
            formData.append(key, String(value).trim().toLowerCase());
          } else {
            formData.append(key, String(value));
          }
        }
      });

      const highlightsArray = parseHighlightsInput(newHighlightsInput);
      const specsObject = parseSpecificationsInput(newSpecificationsInput);
      setNewProduct(prev => ({ ...prev, highlights: highlightsArray, specifications: specsObject }));
      formData.append('highlights', JSON.stringify(highlightsArray));
      formData.append('specifications', JSON.stringify(specsObject));
      formData.append('colorVariants', JSON.stringify(newProduct.colorVariants || []));

      // Append main image file if selected
      if (newProductImage) {
        formData.append('image', newProductImage);
      }

      // Append thumbnails (multiple)
      newProductThumbnails.forEach((file, index) => {
        formData.append('thumbnails', file);
      });

      // Append banners (multiple)
      newProductBanners.forEach((file, index) => {
        formData.append('banners', file);
      });

      // Append videos (multiple)
      newProductVideos.forEach((file, index) => {
        formData.append('videos', file);
      });

      const response = await authenticatedFetch(buildApiUrl('/api/products'), {
        method: 'POST',
        body: formData,
      });
      if (response.ok) {
        setShowAddModal(false);
        setNewProduct({
          title: '',
          description: '',
          shortDescription: '',
          image: '',
          price: 0,
          originalPrice: 0,
          category: '',
          amazonLink: '',
          flipkartLink: '',
          videoUrl: '',
          highlights: [],
          specifications: {},
          rating: 0,
          reviews: 0,
          slug: '',
          featured: false,
        });
        setNewProductImage(null);
        setNewProductImagePreview('');
        setNewProductThumbnails([]);
        setNewProductBanners([]);
        setNewProductVideos([]);
        setNewHighlightsInput('');
        setNewSpecificationsInput('');
        loadProducts();
      } else {
        const error = await response.json();
        alert(error.message || 'Failed to add product');
      }
    } catch (error) {
      console.error('Failed to add product:', error);
      alert('Failed to add product');
    }
  };

  const handleEditProduct = async () => {
    if (!editingProduct) return;
    try {
      const formData = new FormData();

      // Append all product fields
      Object.keys(editingProduct).forEach(key => {
        if (key === 'highlights' || key === 'specifications') {
          return;
        }
        const value = editingProduct[key as keyof typeof editingProduct];
        if (value !== undefined && value !== null && key !== 'id') {
          if (key === 'highlights' || key === 'specifications' || key === 'image' || key === '_id' || key === 'id' || key === 'createdAt' || key === 'updatedAt' || key === '__v' || key === 'media' || key === 'features' || key === 'colorVariants') {
            return;
          }
          if (key === 'category') {
            formData.append(key, String(value).trim().toLowerCase());
          } else if (Array.isArray(value) || typeof value === 'object') {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, String(value));
          }
        }
      });

      const highlightsArray = parseHighlightsInput(editingHighlightsInput);
      const specsObject = parseSpecificationsInput(editingSpecificationsInput);
      setEditingProduct(prev => prev ? { ...prev, highlights: highlightsArray, specifications: specsObject } : null);
      formData.append('highlights', JSON.stringify(highlightsArray));
      formData.append('specifications', JSON.stringify(specsObject));
      formData.append('colorVariants', JSON.stringify(editingProduct.colorVariants || []));

      // Append main image file if selected, otherwise keep existing
      if (editingProductImage) {
        formData.append('image', editingProductImage);
      } else if (editingProduct.image) {
        formData.append('image', editingProduct.image);
      }

      // Append thumbnails (multiple)
      editingProductThumbnails.forEach((file) => {
        formData.append('thumbnails', file);
      });

      // Append banners (multiple)
      editingProductBanners.forEach((file) => {
        formData.append('banners', file);
      });
      formData.append('keepExistingBanners', editingProductMedia.banners.length > 0 ? 'true' : 'false');

      // Append videos (multiple)
      editingProductVideos.forEach((file) => {
        formData.append('videos', file);
      });

      const response = await authenticatedFetch(buildApiUrl(`/api/products/${editingProduct.id}`), {
        method: 'PUT',
        body: formData,
      });
      if (response.ok) {
        setShowEditModal(false);
        setEditingProduct(null);
        setEditingProductImage(null);
        setEditingProductImagePreview('');
        setEditingProductThumbnails([]);
        setEditingProductBanners([]);
        setEditingProductVideos([]);
        loadProducts();
      } else {
        const error = await response.json();
        alert(error.message || 'Failed to update product');
      }
    } catch (error) {
      console.error('Failed to update product:', error);
      alert('Failed to update product');
    }
  };

  const handleDeleteProduct = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      const response = await authenticatedFetch(buildApiUrl(`/api/products/${id}`), {
        method: 'DELETE',
      });
      if (response.ok) {
        loadProducts();
      }
    } catch (error) {
      console.error('Failed to delete product:', error);
    }
  };

  const handleBulkUpload = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await authenticatedFetch(buildApiUrl('/api/products/bulk'), {
        method: 'POST',
        body: formData,
      });
      if (response.ok) {
        setShowUploadModal(false);
        loadProducts();
        alert('Products uploaded successfully!');
      }
    } catch (error) {
      console.error('Failed to upload products:', error);
      alert('Upload failed');
    }
  };

  // No client-side filtering needed - server handles it
  const filteredProducts = products;

  // Get unique categories from products and default categories, sorted alphabetically
  const defaultCategories = ['speakers', 'chargers', 'headphones', 'accessories', 'powerbanks', 'cables'];
  const productCategories = products
    .map(p => p.category)
    .filter(Boolean)
    .filter((cat): cat is string => typeof cat === 'string' && cat.trim() !== '') as string[];

  const availableCategories = Array.from(new Set([
    ...defaultCategories,
    ...productCategories,
  ]))
    .map(cat => cat.trim().toLowerCase()) // Normalize to lowercase
    .filter((cat, index, self) => self.indexOf(cat) === index) // Remove duplicates (case-insensitive)
    .sort(); // Sort alphabetically

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Admin Panel</h1>
            <p className="text-gray-600">Manage products for c nine</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </button>
        </div>

        {/* Actions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          {/* Product Actions */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Product Actions</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Product
              </button>
              <button
                onClick={() => setShowUploadModal(true)}
                className="flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Upload className="w-4 h-4 mr-2" />
                Bulk Upload CSV
              </button>
              <button
                onClick={() => setShowImageUploadModal(true)}
                className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <ImageIcon className="w-4 h-4 mr-2" />
                Upload Images ZIP
              </button>
              <button
                onClick={() => setShowImageSpecs(true)}
                className="flex items-center justify-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Info className="w-4 h-4 mr-2" />
                Image Specs
              </button>
            </div>
          </div>

          {/* Management Actions */}
          <div className="mb-6 pt-6 border-t border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Management</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                onClick={() => navigate('/admin/banners')}
                className="flex items-center justify-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
              >
                <ImageIcon className="w-4 h-4 mr-2" />
                Manage Banners
              </button>
              <button
                onClick={() => navigate('/admin/newsletter')}
                className="flex items-center justify-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <Mail className="w-4 h-4 mr-2" />
                Newsletter Management
              </button>
              <button
                onClick={() => navigate('/admin/orders')}
                className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <ShoppingBag className="w-4 h-4 mr-2" />
                Manage Orders
              </button>
              <button
                onClick={() => setShowPasswordResetModal(true)}
                className="flex items-center justify-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
              >
                <Lock className="w-4 h-4 mr-2" />
                Reset Password
              </button>
            </div>
          </div>

          {/* Destructive Actions */}
          <div className="pt-6 border-t border-gray-200">
            <h3 className="text-sm font-semibold text-red-700 mb-3 uppercase tracking-wide">Danger Zone</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                onClick={() => setShowDeleteProductsConfirm(true)}
                className="flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete All Products
              </button>
              <button
                onClick={() => setShowDeleteImagesConfirm(true)}
                className="flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <ImageIcon className="w-4 h-4 mr-2" />
                Delete All Images
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="sm:w-48">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">All Categories</option>
                {availableCategories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Products Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rating
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                      Loading products...
                    </td>
                  </tr>
                ) : filteredProducts.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                      No products found
                    </td>
                  </tr>
                ) : (
                  filteredProducts.map((product) => {
                    const fallbackImage = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0IiBmaWxsPSIjZWVlZWZmIiByeD0iMTIiLz48dGV4dCB4PSIzMiIgeT0iMzYiIGZpbGw9IiNjY2NjZDAiIGZvbnQtZmFtaWx5PSJBcmlhbCwgSGVsdmV0aWNhLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjEyIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+Tm8gSW1hZ2U8L3RleHQ+PC9zdmc+';
                    const imageSrc = product.image && product.image.trim() !== '' ? product.image : fallbackImage;
                    return (
                      <tr key={product.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => {
                                // Fetch full product data including media
                                Promise.all([
                                  fetch(buildApiUrl(`/api/products/${product.id}`)).then(res => res.json()),
                                  fetch(buildApiUrl(`/api/products/${product.id}/media`)).then(res => res.json()).catch(() => ({ images: [], banners: [], videos: [] }))
                                ])
                                  .then(([fullProduct, mediaData]) => {
                                    setEditingProduct({
                                      ...fullProduct,
                                      id: product.id,
                                      highlights: fullProduct.highlights || [],
                                      specifications: fullProduct.specifications || {},
                                      colorVariants: fullProduct.colorVariants || [],
                                    });
                                    setEditingProductMedia(mediaData.media || mediaData || { images: [], banners: [], videos: [] });
                                    setEditingProductImage(null);
                                    setEditingProductImagePreview('');
                                    setEditingProductThumbnails([]);
                                    setEditingProductBanners([]);
                                    setEditingProductVideos([]);
                                  })
                                  .catch(err => {
                                    console.error('Failed to fetch product:', err);
                                    setEditingProduct(product);
                                    setEditingProductImage(null);
                                    setEditingProductImagePreview('');
                                    setEditingProductMedia({ images: [], banners: [], videos: [] });
                                  });
                                setShowEditModal(true);
                              }}
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteProduct(product.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <img
                              src={imageSrc}
                              alt={product.title}
                              className="h-12 w-12 rounded-lg object-cover"
                              onError={(e) => {
                                if (e.currentTarget.src !== fallbackImage) {
                                  e.currentTarget.src = fallbackImage;
                                }
                              }}
                            />
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {product.title}
                              </div>
                              <div className="text-sm text-gray-500">
                                {product.shortDescription}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {product.category}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          ₹{product.price}
                          {product.originalPrice && product.originalPrice > product.price && (
                            <span className="ml-2 text-sm text-gray-500 line-through">
                              ₹{product.originalPrice}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {product.rating ? `${product.rating} (${product.reviews})` : 'N/A'}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => loadProducts(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => loadProducts(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{(currentPage - 1) * productsPerPage + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * productsPerPage, totalProducts)}
                    </span>{' '}
                    of <span className="font-medium">{totalProducts}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => loadProducts(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    {[...Array(totalPages)].map((_, i) => {
                      const page = i + 1;
                      // Show first page, last page, current page, and pages around current
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => loadProducts(page)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === page
                              ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === currentPage - 2 || page === currentPage + 2) {
                        return <span key={page} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">...</span>;
                      }
                      return null;
                    })}
                    <button
                      onClick={() => loadProducts(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Add Product Modal - Enhanced with all fields */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-2xl font-semibold mb-6">Add New Product</h2>
                <div className="space-y-6">
                  {/* Basic Information */}
                  <div className="border-b pb-4">
                    <h3 className="text-lg font-semibold mb-3">Basic Information</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                        <input
                          type="text"
                          value={newProduct.title}
                          onChange={(e) => setNewProduct(prev => ({ ...prev, title: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
                        <input
                          type="text"
                          value={newProduct.slug || (newProduct.title ? newProduct.title.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') : '')}
                          onChange={(e) => setNewProduct(prev => ({ ...prev, slug: e.target.value }))}
                          placeholder="Auto-generated from title"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Short Description</label>
                      <input
                        type="text"
                        value={newProduct.shortDescription || ''}
                        onChange={(e) => setNewProduct(prev => ({ ...prev, shortDescription: e.target.value }))}
                        placeholder="Brief product description"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                      <textarea
                        value={newProduct.description}
                        onChange={(e) => setNewProduct(prev => ({ ...prev, description: e.target.value }))}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Pricing & Category */}
                  <div className="border-b pb-4">
                    <h3 className="text-lg font-semibold mb-3">Pricing & Category</h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Price *</label>
                        <input
                          type="number"
                          value={newProduct.price}
                          onChange={(e) => setNewProduct(prev => ({ ...prev, price: Number(e.target.value) }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Original Price</label>
                        <input
                          type="number"
                          value={newProduct.originalPrice || ''}
                          onChange={(e) => setNewProduct(prev => ({ ...prev, originalPrice: Number(e.target.value) || undefined }))}
                          placeholder="For discount display"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                        <div className="relative">
                          <select
                            value={newProduct.category}
                            onChange={(e) => setNewProduct(prev => ({ ...prev, category: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white pr-10"
                          >
                            <option value="">Select or type a category</option>
                            {availableCategories.map(category => (
                              <option key={category} value={category}>
                                {category.charAt(0).toUpperCase() + category.slice(1)}
                              </option>
                            ))}
                          </select>
                          <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                        <div className="mt-2">
                          <input
                            type="text"
                            value={newProduct.category}
                            onChange={(e) => setNewProduct(prev => ({ ...prev, category: e.target.value.trim().toLowerCase() }))}
                            onBlur={(e) => {
                              // Normalize on blur
                              const value = e.target.value.trim().toLowerCase();
                              if (value) {
                                setNewProduct(prev => ({ ...prev, category: value }));
                              }
                            }}
                            placeholder="Or type a new category name"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                          />
                          <p className="text-xs text-gray-500 mt-1">Select from dropdown above or type a new category name</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Links & Media */}
                  <div className="border-b pb-4">
                    <h3 className="text-lg font-semibold mb-3">Links & Media</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Amazon Link</label>
                        <input
                          type="url"
                          value={newProduct.amazonLink || ''}
                          onChange={(e) => setNewProduct(prev => ({ ...prev, amazonLink: e.target.value }))}
                          placeholder="https://amazon.in/dp/..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Flipkart Link</label>
                        <input
                          type="url"
                          value={newProduct.flipkartLink || ''}
                          onChange={(e) => setNewProduct(prev => ({ ...prev, flipkartLink: e.target.value }))}
                          placeholder="https://flipkart.com/p/..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Video URL</label>
                      <input
                        type="url"
                        value={newProduct.videoUrl || ''}
                        onChange={(e) => setNewProduct(prev => ({ ...prev, videoUrl: e.target.value }))}
                        placeholder="YouTube/Vimeo URL or direct video link"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Product Images */}
                  <div className="border-b pb-4">
                    <h3 className="text-lg font-semibold mb-3">Product Images</h3>
                    <div className="space-y-4">
                      {/* Main Image */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Main Product Image</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setNewProductImage(file);
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                setNewProductImagePreview(reader.result as string);
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                        {newProductImagePreview && (
                          <div className="mt-2">
                            <img
                              src={newProductImagePreview}
                              alt="Preview"
                              className="h-32 w-32 object-cover rounded-lg border border-gray-300"
                            />
                          </div>
                        )}
                        <input
                          type="url"
                          placeholder="Or enter image URL"
                          value={newProduct.image}
                          onChange={(e) => setNewProduct(prev => ({ ...prev, image: e.target.value }))}
                          className="w-full px-3 py-2 mt-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>

                      {/* Multiple Thumbnails */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Additional Thumbnails (up to 10)</label>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            setNewProductThumbnails(files.slice(0, 10));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                        {newProductThumbnails.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {newProductThumbnails.map((file, index) => (
                              <div key={index} className="relative">
                                <img
                                  src={URL.createObjectURL(file)}
                                  alt={`Thumbnail ${index + 1}`}
                                  className="h-20 w-20 object-cover rounded-lg border border-gray-300"
                                />
                                <button
                                  onClick={() => setNewProductThumbnails(prev => prev.filter((_, i) => i !== index))}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Multiple Banners */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Product Banners (up to 10)</label>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            setNewProductBanners(files.slice(0, 10));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                        {newProductBanners.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {newProductBanners.map((file, index) => (
                              <div key={index} className="relative">
                                <img
                                  src={URL.createObjectURL(file)}
                                  alt={`Banner ${index + 1}`}
                                  className="h-20 w-20 object-cover rounded-lg border border-gray-300"
                                />
                                <button
                                  onClick={() => setNewProductBanners(prev => prev.filter((_, i) => i !== index))}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Video Uploads */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Video Files (up to 5, MP4/WEBM/MOV)</label>
                        <input
                          type="file"
                          accept="video/*"
                          multiple
                          onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            setNewProductVideos(files.slice(0, 5));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                        {newProductVideos.length > 0 && (
                          <div className="mt-2 space-y-2">
                            {newProductVideos.map((file, index) => (
                              <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                <span className="text-sm text-gray-700">{file.name}</span>
                                <button
                                  onClick={() => setNewProductVideos(prev => prev.filter((_, i) => i !== index))}
                                  className="text-red-500 text-sm hover:text-red-700"
                                >
                                  Remove
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Product Color Variants */}
                  <ColorVariantsSection
                    variants={newProduct.colorVariants || []}
                    onChange={(variants) => setNewProduct(prev => ({ ...prev, colorVariants: variants }))}
                    authenticatedFetch={authenticatedFetch}
                  />

                  {/* Highlights & Specifications */}
                  <div className="border-b pb-4">
                    <h3 className="text-lg font-semibold mb-3">Highlights & Specifications</h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Highlights (pipe-separated or one per line)</label>
                      <textarea
                        value={newHighlightsInput}
                        onChange={(e) => setNewHighlightsInput(e.target.value)}
                        onBlur={() =>
                          setNewProduct(prev => ({
                            ...prev,
                            highlights: parseHighlightsInput(newHighlightsInput),
                          }))
                        }
                        rows={3}
                        placeholder="80 Hours Battery\nANC Technology\nDual Device Pairing"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Specifications (JSON format or key:value pairs)</label>
                      <textarea
                        value={newSpecificationsInput}
                        onChange={(e) => setNewSpecificationsInput(e.target.value)}
                        onBlur={() =>
                          setNewProduct(prev => ({
                            ...prev,
                            specifications: parseSpecificationsInput(newSpecificationsInput),
                          }))
                        }
                        rows={6}
                        placeholder={"Connectivity: Bluetooth 5.0\nBattery Life: 8 hours\nRange: 10 meters"}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus-border-transparent font-mono text-sm"
                      />
                    </div>
                  </div>

                  {/* Ratings & Reviews */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3">Ratings & Reviews</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Rating</label>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="5"
                          value={newProduct.rating !== undefined && newProduct.rating !== null ? newProduct.rating : ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            setNewProduct(prev => ({ ...prev, rating: val !== '' ? Number(val) : undefined }));
                          }}
                          placeholder="4.5"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Reviews Count</label>
                        <input
                          type="number"
                          min="0"
                          value={newProduct.reviews !== undefined && newProduct.reviews !== null ? newProduct.reviews : ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            setNewProduct(prev => ({ ...prev, reviews: val !== '' ? Number(val) : undefined }));
                          }}
                          placeholder="1250"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                    <div className="mt-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={newProduct.featured || false}
                          onChange={(e) => setNewProduct(prev => ({ ...prev, featured: e.target.checked }))}
                          className="mr-2 w-4 h-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <span className="text-sm font-medium text-gray-700">Featured Product (This will display on home page)</span>
                      </label>
                    </div>
                    <div className="mt-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={newProduct.sellOnSite ?? true}
                          onChange={(e) => setNewProduct(prev => ({ ...prev, sellOnSite: e.target.checked }))}
                          className="mr-2 w-4 h-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <span className="text-sm font-medium text-gray-700">Sell on our Website (Uncheck to only show Amazon/Flipkart links)</span>
                      </label>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => {
                      setShowAddModal(false);
                      setNewProductImage(null);
                      setNewProductImagePreview('');
                      setNewProductThumbnails([]);
                      setNewProductBanners([]);
                      setNewProductVideos([]);
                      setNewHighlightsInput('');
                      setNewSpecificationsInput('');
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddProduct}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                  >
                    Add Product
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Product Modal - Enhanced with all fields */}
        {showEditModal && editingProduct && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-2xl font-semibold mb-6">Edit Product</h2>
                <div className="space-y-6">
                  {/* Basic Information */}
                  <div className="border-b pb-4">
                    <h3 className="text-lg font-semibold mb-3">Basic Information</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                        <input
                          type="text"
                          value={editingProduct.title}
                          onChange={(e) => setEditingProduct(prev => prev ? { ...prev, title: e.target.value } : null)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
                        <input
                          type="text"
                          value={editingProduct.slug || ''}
                          onChange={(e) => setEditingProduct(prev => prev ? { ...prev, slug: e.target.value } : null)}
                          placeholder="Auto-generated from title"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Short Description</label>
                      <input
                        type="text"
                        value={editingProduct.shortDescription || ''}
                        onChange={(e) => setEditingProduct(prev => prev ? { ...prev, shortDescription: e.target.value } : null)}
                        placeholder="Brief product description"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                      <textarea
                        value={editingProduct.description}
                        onChange={(e) => setEditingProduct(prev => prev ? { ...prev, description: e.target.value } : null)}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Pricing & Category */}
                  <div className="border-b pb-4">
                    <h3 className="text-lg font-semibold mb-3">Pricing & Category</h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Price *</label>
                        <input
                          type="number"
                          value={editingProduct.price}
                          onChange={(e) => setEditingProduct(prev => prev ? { ...prev, price: Number(e.target.value) } : null)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Original Price</label>
                        <input
                          type="number"
                          value={editingProduct.originalPrice || ''}
                          onChange={(e) => setEditingProduct(prev => prev ? { ...prev, originalPrice: Number(e.target.value) || undefined } : null)}
                          placeholder="For discount display"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                        <div className="relative">
                          <select
                            value={editingProduct.category || ''}
                            onChange={(e) => setEditingProduct(prev => prev ? { ...prev, category: e.target.value } : null)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white pr-10"
                          >
                            <option value="">Select or type a category</option>
                            {availableCategories.map(category => (
                              <option key={category} value={category}>
                                {category.charAt(0).toUpperCase() + category.slice(1)}
                              </option>
                            ))}
                          </select>
                          <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                        <div className="mt-2">
                          <input
                            type="text"
                            value={editingProduct.category || ''}
                            onChange={(e) => setEditingProduct(prev => prev ? { ...prev, category: e.target.value.trim().toLowerCase() } : null)}
                            onBlur={(e) => {
                              // Normalize on blur
                              const value = e.target.value.trim().toLowerCase();
                              if (value && editingProduct) {
                                setEditingProduct(prev => prev ? { ...prev, category: value } : null);
                              }
                            }}
                            placeholder="Or type a new category name"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                          />
                          <p className="text-xs text-gray-500 mt-1">Select from dropdown above or type a new category name</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Links & Media */}
                  <div className="border-b pb-4">
                    <h3 className="text-lg font-semibold mb-3">Links & Media</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Amazon Link</label>
                        <input
                          type="url"
                          value={editingProduct.amazonLink || ''}
                          onChange={(e) => setEditingProduct(prev => prev ? { ...prev, amazonLink: e.target.value } : null)}
                          placeholder="https://amazon.in/dp/..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Flipkart Link</label>
                        <input
                          type="url"
                          value={editingProduct.flipkartLink || ''}
                          onChange={(e) => setEditingProduct(prev => prev ? { ...prev, flipkartLink: e.target.value } : null)}
                          placeholder="https://flipkart.com/p/..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Video URL</label>
                      <input
                        type="url"
                        value={editingProduct.videoUrl || ''}
                        onChange={(e) => setEditingProduct(prev => prev ? { ...prev, videoUrl: e.target.value } : null)}
                        placeholder="YouTube/Vimeo URL or direct video link"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Product Images */}
                  <div className="border-b pb-4">
                    <h3 className="text-lg font-semibold mb-3">Product Images</h3>
                    <div className="space-y-4">
                      {/* Main Image */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Main Product Image</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setEditingProductImage(file);
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                setEditingProductImagePreview(reader.result as string);
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                        {editingProductImagePreview && (
                          <div className="mt-2">
                            <img
                              src={editingProductImagePreview}
                              alt="Preview"
                              className="h-32 w-32 object-cover rounded-lg border border-gray-300"
                            />
                          </div>
                        )}
                        {!editingProductImage && editingProduct.image && (
                          <div className="mt-2">
                            <p className="text-xs text-gray-500 mb-1">Current image:</p>
                            <img
                              src={`${editingProduct.image}?v=${bannerCacheBust}`}
                              alt="Current"
                              className="h-32 w-32 object-cover rounded-lg border border-gray-300"
                            />
                          </div>
                        )}
                        <input
                          type="url"
                          placeholder="Or enter image URL"
                          value={editingProduct.image}
                          onChange={(e) => setEditingProduct(prev => prev ? { ...prev, image: e.target.value } : null)}
                          className="w-full px-3 py-2 mt-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>

                      {/* Multiple Thumbnails */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Additional Thumbnails (up to 10)</label>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            setEditingProductThumbnails(files.slice(0, 10));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                        {/* Existing Thumbnails */}
                        {editingProductMedia.images && editingProductMedia.images.length > 0 && (
                          <div className="mt-3">
                            <p className="text-xs text-gray-500 mb-2">Existing Thumbnails:</p>
                            <div className="flex flex-wrap gap-2">
                              {editingProductMedia.images.map((img, index) => {
                                const filename = img.split('/').pop() || '';
                                return (
                                  <div key={index} className="relative">
                                    <img
                                      src={img}
                                      alt={`Thumbnail ${index + 1}`}
                                      className="h-20 w-20 object-cover rounded-lg border border-gray-300"
                                    />
                                    <button
                                      onClick={async () => {
                                        if (!editingProduct?.id) return;
                                        setDeletingMedia(img);
                                        try {
                                          const response = await authenticatedFetch(
                                            buildApiUrl(`/api/products/${editingProduct.id}/media/images/${encodeURIComponent(filename)}`),
                                            {
                                              method: 'DELETE',
                                            }
                                          );
                                          if (response.ok) {
                                            setEditingProductMedia(prev => ({
                                              ...prev,
                                              images: prev.images.filter(i => i !== img)
                                            }));
                                          } else {
                                            const errorData = await response.json().catch(() => ({ message: 'Failed to delete thumbnail' }));
                                            if (response.status === 401) {
                                              alert('Your session has expired. Please log in again.');
                                            } else {
                                              alert(errorData.message || 'Failed to delete thumbnail');
                                            }
                                          }
                                        } catch (error: any) {
                                          console.error('Failed to delete thumbnail:', error);
                                          if (error.message === 'Session expired' || error.message === 'No token found') {
                                            alert('Your session has expired. Please log in again.');
                                          } else {
                                            alert('Failed to delete thumbnail. Please try again.');
                                          }
                                        } finally {
                                          setDeletingMedia(null);
                                        }
                                      }}
                                      disabled={deletingMedia === img}
                                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600 disabled:opacity-50"
                                    >
                                      {deletingMedia === img ? '...' : '×'}
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {/* New Thumbnails */}
                        {editingProductThumbnails.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs text-gray-500 mb-2">New Thumbnails to Upload:</p>
                            <div className="flex flex-wrap gap-2">
                              {editingProductThumbnails.map((file, index) => (
                                <div key={index} className="relative">
                                  <img
                                    src={URL.createObjectURL(file)}
                                    alt={`Thumbnail ${index + 1}`}
                                    className="h-20 w-20 object-cover rounded-lg border border-gray-300"
                                  />
                                  <button
                                    onClick={() => setEditingProductThumbnails(prev => prev.filter((_, i) => i !== index))}
                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Multiple Banners */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Product Banners (up to 10)</label>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            setEditingProductBanners(files.slice(0, 10));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                        {/* Existing Banners */}
                        {editingProductMedia.banners && editingProductMedia.banners.length > 0 && (
                          <div className="mt-3">
                            <p className="text-xs text-gray-500 mb-2">Existing Banners:</p>
                            <div className="flex flex-wrap gap-2">
                              {editingProductMedia.banners.map((banner, index) => {
                                const filename = (banner.split('/').pop() || '').split('?')[0];
                                const displayUrl = `${banner}${banner.includes('?') ? '&' : '?'}v=${bannerCacheBust}`;
                                return (
                                  <div key={index} className="relative">
                                    <img
                                      src={displayUrl}
                                      alt={`Banner ${index + 1}`}
                                      className="h-20 w-20 object-cover rounded-lg border border-gray-300"
                                    />
                                    <button
                                      onClick={async () => {
                                        if (!editingProduct?.id) return;
                                        setDeletingMedia(banner);
                                        try {
                                          const response = await authenticatedFetch(
                                            buildApiUrl(`/api/products/${editingProduct.id}/media/banners/${encodeURIComponent(filename)}`),
                                            {
                                              method: 'DELETE',
                                            }
                                          );
                                          if (response.ok) {
                                            setEditingProductMedia(prev => ({
                                              ...prev,
                                              banners: prev.banners.filter(b => b !== banner)
                                            }));
                                          } else {
                                            const errorData = await response.json().catch(() => ({ message: 'Failed to delete banner' }));
                                            if (response.status === 401) {
                                              alert('Your session has expired. Please log in again.');
                                            } else {
                                              alert(errorData.message || 'Failed to delete banner');
                                            }
                                          }
                                        } catch (error: any) {
                                          console.error('Failed to delete banner:', error);
                                          if (error.message === 'Session expired' || error.message === 'No token found') {
                                            alert('Your session has expired. Please log in again.');
                                          } else {
                                            alert('Failed to delete banner. Please try again.');
                                          }
                                        } finally {
                                          setDeletingMedia(null);
                                        }
                                      }}
                                      disabled={deletingMedia === banner}
                                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600 disabled:opacity-50"
                                    >
                                      {deletingMedia === banner ? '...' : '×'}
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {/* New Banners */}
                        {editingProductBanners.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs text-gray-500 mb-2">New Banners to Upload:</p>
                            <div className="flex flex-wrap gap-2">
                              {editingProductBanners.map((file, index) => (
                                <div key={index} className="relative">
                                  <img
                                    src={URL.createObjectURL(file)}
                                    alt={`Banner ${index + 1}`}
                                    className="h-20 w-20 object-cover rounded-lg border border-gray-300"
                                  />
                                  <button
                                    onClick={() => setEditingProductBanners(prev => prev.filter((_, i) => i !== index))}
                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Video Uploads */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Video Files (up to 5, MP4/WEBM/MOV)</label>
                        <input
                          type="file"
                          accept="video/*"
                          multiple
                          onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            setEditingProductVideos(files.slice(0, 5));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                        {/* Existing Videos */}
                        {editingProductMedia.videos && editingProductMedia.videos.length > 0 && (
                          <div className="mt-3">
                            <p className="text-xs text-gray-500 mb-2">Existing Videos:</p>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                              {editingProductMedia.videos.map((video, index) => {
                                const filename = video.split('/').pop() || '';
                                return (
                                  <div key={index} className="relative group">
                                    <div className="aspect-video bg-gray-900 rounded-lg overflow-hidden border border-gray-300">
                                      <video
                                        src={video}
                                        className="w-full h-full object-cover"
                                        muted
                                        playsInline
                                        onMouseEnter={(e) => {
                                          try {
                                            if (e.currentTarget && e.currentTarget.readyState >= 2) {
                                              e.currentTarget.play();
                                            }
                                          } catch (error) {
                                            // Ignore play errors
                                          }
                                        }}
                                        onMouseLeave={(e) => {
                                          try {
                                            if (e.currentTarget) {
                                              e.currentTarget.pause();
                                              e.currentTarget.currentTime = 0;
                                            }
                                          } catch (error) {
                                            // Ignore pause errors
                                          }
                                        }}
                                      />
                                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 group-hover:bg-opacity-40 transition-colors pointer-events-none">
                                        <Play className="w-8 h-8 text-white" />
                                      </div>
                                    </div>
                                    <div className="absolute top-1 right-1 flex gap-1">
                                      <button
                                        onClick={() => {
                                          setPlayingVideo(video);
                                        }}
                                        className="bg-blue-500 text-white rounded-full p-1.5 hover:bg-blue-600 transition-colors shadow-md"
                                        title="Play video"
                                      >
                                        <Play className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={async () => {
                                          if (!editingProduct?.id) return;
                                          setDeletingMedia(video);
                                          try {
                                            const response = await authenticatedFetch(
                                              buildApiUrl(`/api/products/${editingProduct.id}/media/videos/${encodeURIComponent(filename)}`),
                                              {
                                                method: 'DELETE',
                                              }
                                            );
                                            if (response.ok) {
                                              setEditingProductMedia(prev => ({
                                                ...prev,
                                                videos: prev.videos.filter(v => v !== video)
                                              }));
                                            } else {
                                              const errorData = await response.json().catch(() => ({ message: 'Failed to delete video' }));
                                              if (response.status === 401) {
                                                alert('Your session has expired. Please log in again.');
                                              } else {
                                                alert(errorData.message || 'Failed to delete video');
                                              }
                                            }
                                          } catch (error: any) {
                                            console.error('Failed to delete video:', error);
                                            if (error.message === 'Session expired' || error.message === 'No token found') {
                                              alert('Your session has expired. Please log in again.');
                                            } else {
                                              alert('Failed to delete video. Please try again.');
                                            }
                                          } finally {
                                            setDeletingMedia(null);
                                          }
                                        }}
                                        disabled={deletingMedia === video}
                                        className="bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 disabled:opacity-50 transition-colors shadow-md"
                                        title="Delete video"
                                      >
                                        {deletingMedia === video ? '...' : '×'}
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {/* New Videos */}
                        {editingProductVideos.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs text-gray-500 mb-2">New Videos to Upload:</p>
                            <div className="space-y-2">
                              {editingProductVideos.map((file, index) => (
                                <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                  <span className="text-sm text-gray-700">{file.name}</span>
                                  <button
                                    onClick={() => setEditingProductVideos(prev => prev.filter((_, i) => i !== index))}
                                    className="text-red-500 text-sm hover:text-red-700"
                                  >
                                    Remove
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Product Color Variants */}
                  <ColorVariantsSection
                    variants={editingProduct?.colorVariants || []}
                    onChange={(variants) => setEditingProduct(prev => prev ? { ...prev, colorVariants: variants } : null)}
                    authenticatedFetch={authenticatedFetch}
                  />

                  {/* Highlights & Specifications */}
                  <div className="border-b pb-4">
                    <h3 className="text-lg font-semibold mb-3">Highlights & Specifications</h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Highlights (pipe-separated or one per line)</label>
                      <textarea
                        value={editingHighlightsInput}
                        onChange={(e) => setEditingHighlightsInput(e.target.value)}
                        onBlur={() =>
                          setEditingProduct(prev => prev ? {
                            ...prev,
                            highlights: parseHighlightsInput(editingHighlightsInput),
                          } : null)
                        }
                        rows={3}
                        placeholder="80 Hours Battery\nANC Technology\nDual Device Pairing"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Specifications (JSON format or key:value pairs)</label>
                      <textarea
                        value={editingSpecificationsInput}
                        onChange={(e) => setEditingSpecificationsInput(e.target.value)}
                        onBlur={() =>
                          setEditingProduct(prev => prev ? {
                            ...prev,
                            specifications: parseSpecificationsInput(editingSpecificationsInput),
                          } : null)
                        }
                        rows={6}
                        placeholder={"Connectivity: Bluetooth 5.0\nBattery Life: 8 hours\nRange: 10 meters"}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus-border-transparent font-mono text-sm"
                      />
                    </div>
                  </div>

                  {/* Ratings & Reviews */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3">Ratings & Reviews</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Rating</label>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="5"
                          value={editingProduct.rating !== undefined && editingProduct.rating !== null ? editingProduct.rating : ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            setEditingProduct(prev => prev ? { ...prev, rating: val !== '' ? Number(val) : undefined } : null);
                          }}
                          placeholder="4.5"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Reviews Count</label>
                        <input
                          type="number"
                          min="0"
                          value={editingProduct.reviews !== undefined && editingProduct.reviews !== null ? editingProduct.reviews : ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            setEditingProduct(prev => prev ? { ...prev, reviews: val !== '' ? Number(val) : undefined } : null);
                          }}
                          placeholder="1250"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                    <div className="mt-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editingProduct.featured || false}
                          onChange={(e) => setEditingProduct(prev => prev ? { ...prev, featured: e.target.checked } : null)}
                          className="mr-2 w-4 h-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <span className="text-sm font-medium text-gray-700">Featured Product (This will display on home page)</span>
                      </label>
                    </div>
                    <div className="mt-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editingProduct.sellOnSite ?? true}
                          onChange={(e) => setEditingProduct(prev => prev ? { ...prev, sellOnSite: e.target.checked } : null)}
                          className="mr-2 w-4 h-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <span className="text-sm font-medium text-gray-700">Sell on our Website (Uncheck to only show Amazon/Flipkart links)</span>
                      </label>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingProduct(null);
                      setEditingProductImage(null);
                      setEditingProductImagePreview('');
                      setEditingProductThumbnails([]);
                      setEditingProductBanners([]);
                      setEditingProductVideos([]);
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleEditProduct}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                  >
                    Update Product
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Video Playback Modal */}
        {playingVideo && (
          <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center p-4 z-50" onClick={() => setPlayingVideo(null)}>
            <div className="relative max-w-7xl w-full max-h-[90vh] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => setPlayingVideo(null)}
                className="absolute top-4 right-4 bg-white text-gray-900 rounded-full p-2 hover:bg-gray-200 transition-colors z-10"
                title="Close"
              >
                ×
              </button>
              <video
                src={playingVideo}
                controls
                autoPlay
                className="max-w-full max-h-[90vh] rounded-lg"
              />
            </div>
          </div>
        )}

        {/* Bulk Upload Modal */}
        {showUploadModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-xl font-semibold mb-4">Bulk Upload CSV</h2>
                <div className="mb-4">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleBulkUpload(file);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div className="text-sm text-gray-600 mb-4 space-y-2">
                  <p className="font-semibold">CSV format should include columns:</p>
                  <p className="pl-4">title, description, shortDescription, image, price, originalPrice, category, amazonLink, flipkartLink, rating, reviews</p>
                </div>
                <div className="flex flex-col gap-2 mb-4">
                  <button
                    onClick={downloadSampleCSV}
                    className="flex items-center justify-center px-4 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Sample CSV
                  </button>
                  <button
                    onClick={downloadTestCSV}
                    className="flex items-center justify-center px-4 py-2 bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Test CSV (100+ Products)
                  </button>
                  <button
                    onClick={() => window.open(buildApiUrl('/api/products/test-images-zip'), '_blank')}
                    className="flex items-center justify-center px-4 py-2 bg-purple-100 text-purple-700 rounded-md hover:bg-purple-200 transition-colors"
                  >
                    <ImageIcon className="w-4 h-4 mr-2" />
                    Download Images ZIP (100+ Products)
                  </button>
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  <p>💡 Tip: Upload the CSV first, then upload the images ZIP. Image names match product titles.</p>
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={() => setShowUploadModal(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Image ZIP Upload Modal */}
        {showImageUploadModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-xl font-semibold mb-4">Upload Product Images (ZIP)</h2>
                <div className="mb-4">
                  <input
                    type="file"
                    accept=".zip"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setSelectedZipFile(file);
                      }
                    }}
                    disabled={isUploadingImages}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                  {selectedZipFile && (
                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                      <p className="text-sm text-green-800">
                        <span className="font-semibold">Selected:</span> {selectedZipFile.name}
                      </p>
                      <p className="text-xs text-green-600 mt-1">
                        Size: {(selectedZipFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  )}
                </div>
                <div className="text-sm text-gray-600 mb-4 space-y-2">
                  <p className="font-semibold">Instructions:</p>
                  <ul className="list-disc list-inside space-y-1 pl-2">
                    <li>Create a ZIP file containing product images</li>
                    <li>Name images using product title with underscores (e.g., wireless_speaker.jpg)</li>
                    <li>Spaces in product names should be replaced with underscores</li>
                    <li>Images will be automatically resized to multiple sizes</li>
                    <li>Supported formats: JPG, JPEG, PNG, WEBP</li>
                    <li>Max file size: 100MB</li>
                  </ul>
                  <p className="mt-2 font-semibold">Example:</p>
                  <p className="pl-4">portable_speaker.jpg, fast_charger.png, wireless_headphones.webp</p>
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="font-semibold text-primary-600">📦 Quick Setup:</p>
                    <ol className="list-decimal list-inside space-y-1 pl-2 mt-1">
                      <li>Download Test CSV (100+ products)</li>
                      <li>Upload the CSV in "Bulk Upload CSV"</li>
                      <li>Download Images ZIP (matches CSV products)</li>
                      <li>Upload the Images ZIP here</li>
                      <li>Images will automatically match products by name!</li>
                    </ol>
                  </div>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowImageUploadModal(false);
                      setSelectedZipFile(null);
                    }}
                    disabled={isUploadingImages}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  {selectedZipFile && (
                    <button
                      onClick={() => handleImageZipUpload(selectedZipFile)}
                      disabled={isUploadingImages}
                      className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      {isUploadingImages ? (
                        <>
                          <span className="animate-spin mr-2">⏳</span>
                          Uploading...
                        </>
                      ) : (
                        'Upload Images'
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Image Specifications Modal */}
        {showImageSpecs && imageSpecs && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-xl font-semibold mb-4">Image Size Specifications</h2>

                {/* Product Image Specs */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Product Images</h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="font-medium text-sm">Thumbnail</p>
                        <p className="text-sm text-gray-600">200x200px</p>
                        <p className="text-xs text-gray-500">Product thumbnails in lists</p>
                      </div>
                      <div>
                        <p className="font-medium text-sm">Small</p>
                        <p className="text-sm text-gray-600">400x400px</p>
                        <p className="text-xs text-gray-500">Small product previews</p>
                      </div>
                      <div>
                        <p className="font-medium text-sm">Medium</p>
                        <p className="text-sm text-gray-600">800x800px</p>
                        <p className="text-xs text-gray-500">Standard product images</p>
                      </div>
                      <div>
                        <p className="font-medium text-sm">Large</p>
                        <p className="text-sm text-gray-600">1200x1200px</p>
                        <p className="text-xs text-gray-500">Full-size product images</p>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-sm"><strong>Format:</strong> {imageSpecs.product?.format}</p>
                      <p className="text-sm"><strong>Max Size:</strong> {imageSpecs.product?.maxSize}</p>
                      <p className="text-sm"><strong>Naming:</strong> {imageSpecs.product?.naming}</p>
                    </div>
                  </div>
                </div>

                {/* Banner Specs */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Banner Images</h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-1 gap-3">
                      <div>
                        <p className="font-medium text-sm">Hero Banner</p>
                        <p className="text-sm text-gray-600">1920x600px</p>
                        <p className="text-xs text-gray-500">Main homepage hero banner</p>
                      </div>
                      <div>
                        <p className="font-medium text-sm">Category Banner</p>
                        <p className="text-sm text-gray-600">1200x400px</p>
                        <p className="text-xs text-gray-500">Category page banners</p>
                      </div>
                      <div>
                        <p className="font-medium text-sm">Promotional Banner</p>
                        <p className="text-sm text-gray-600">800x400px</p>
                        <p className="text-xs text-gray-500">Promotional banners</p>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-sm"><strong>Format:</strong> {imageSpecs.banner?.format}</p>
                      <p className="text-sm"><strong>Max Size:</strong> {imageSpecs.banner?.maxSize}</p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={() => setShowImageSpecs(false)}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete All Images Confirmation Modal */}
        {showDeleteImagesConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                    <ImageIcon className="w-6 h-6 text-orange-600" />
                  </div>
                  <div className="ml-4">
                    <h2 className="text-xl font-semibold text-gray-900">Delete All Images</h2>
                  </div>
                </div>

                <div className="mb-6">
                  <p className="text-sm text-gray-600 mb-4">
                    Are you sure you want to delete <strong>ALL</strong> uploaded images?
                  </p>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-orange-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-orange-800">Warning</h3>
                        <div className="mt-2 text-sm text-orange-700">
                          <ul className="list-disc list-inside space-y-1">
                            <li>This will delete <strong>ALL IMAGES</strong> from local storage</li>
                            <li>This will delete <strong>ALL IMAGES</strong> from Azure Blob Storage (if configured)</li>
                            <li>Product data will remain, but product images will be removed</li>
                            <li>All image files will be permanently removed</li>
                            <li>This action cannot be undone!</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowDeleteImagesConfirm(false)}
                    disabled={isDeletingImages}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteAllImages}
                    disabled={isDeletingImages}
                    className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {isDeletingImages ? (
                      <>
                        <span className="animate-spin mr-2">⏳</span>
                        Deleting...
                      </>
                    ) : (
                      <>
                        <ImageIcon className="w-4 h-4 mr-2" />
                        Delete All Images
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete All Products Confirmation Modal */}
        {showDeleteProductsConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <Trash2 className="w-6 h-6 text-red-600" />
                  </div>
                  <div className="ml-4">
                    <h2 className="text-xl font-semibold text-gray-900">Delete All Products</h2>
                  </div>
                </div>

                <div className="mb-6">
                  <p className="text-sm text-gray-600 mb-4">
                    Are you sure you want to delete <strong>ALL</strong> products from the database?
                  </p>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">Warning</h3>
                        <div className="mt-2 text-sm text-red-700">
                          <ul className="list-disc list-inside space-y-1">
                            <li>This will delete <strong>ALL PRODUCTS</strong> from the database</li>
                            <li>This will delete all associated images from local storage</li>
                            <li>This will delete all associated images from Azure Blob Storage (if configured)</li>
                            <li>All product data will be permanently removed</li>
                            <li>This action cannot be undone!</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowDeleteProductsConfirm(false)}
                    disabled={isDeletingProducts}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteAllProducts}
                    disabled={isDeletingProducts}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {isDeletingProducts ? (
                      <>
                        <span className="animate-spin mr-2">⏳</span>
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete All Products
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Password Reset Modal */}
        {showPasswordResetModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="p-6">
                <h2 className="text-xl font-semibold mb-4">Reset Password</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                    <input
                      type="password"
                      value={passwordResetData.currentPassword}
                      onChange={(e) => setPasswordResetData(prev => ({ ...prev, currentPassword: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter current password"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                    <input
                      type="password"
                      value={passwordResetData.newPassword}
                      onChange={(e) => setPasswordResetData(prev => ({ ...prev, newPassword: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter new password (min 6 characters)"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                    <input
                      type="password"
                      value={passwordResetData.confirmPassword}
                      onChange={(e) => setPasswordResetData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Confirm new password"
                    />
                  </div>
                  {passwordResetError && (
                    <div className="text-red-600 text-sm">{passwordResetError}</div>
                  )}
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => {
                      setShowPasswordResetModal(false);
                      setPasswordResetData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                      setPasswordResetError('');
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handlePasswordReset}
                    disabled={isResettingPassword}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50"
                  >
                    {isResettingPassword ? 'Resetting...' : 'Reset Password'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
